import React from 'react';
import { Box, CircularProgress, Sheet, Stack, Typography, Chip } from '@mui/joy';
import {
  PhoneIncoming,
  PhoneOutgoing,
  Voicemail,
  PhoneX,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import type { CallLog } from '../../../types/inbox';
import { useRecordingAudio } from '../../../hooks/useRecordingAudio';

interface CallTimelineCardProps {
  call: CallLog;
}

/** Seconds to m:ss. */
const formatDuration = (seconds?: number | null): string | null => {
  if (!seconds || Number.isNaN(seconds)) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatPhone = (phone?: string): string => {
  if (!phone) return 'Unknown';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

/** Colour by how the call ended, so a missed call reads at a glance. */
const RESULT_COLOR: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  answered: 'success',
  voicemail: 'warning',
  missed: 'danger',
  'no-answer': 'neutral',
  busy: 'neutral',
  failed: 'danger',
  initiated: 'neutral',
};

export const CallTimelineCard: React.FC<CallTimelineCardProps> = ({ call }) => {
  const isInbound = call.direction === 'inbound';
  const duration = formatDuration(call.duration);
  const hasVoicemail = Boolean(call.recording_url || call.transcription);
  const counterparty = isInbound ? call.from_number : call.to_number;
  const audio = useRecordingAudio(call.id, Boolean(call.recording_url));

  const Icon = call.result === 'voicemail'
    ? Voicemail
    : call.result === 'missed'
      ? PhoneX
      : isInbound
        ? PhoneIncoming
        : PhoneOutgoing;

  const started = call.started_at ? new Date(call.started_at) : null;

  return (
    <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Sheet
          variant="soft"
          color={RESULT_COLOR[call.result ?? ''] ?? 'neutral'}
          sx={{
            p: 1,
            borderRadius: 'md',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={18} />
        </Sheet>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography level="title-sm">
              {isInbound ? 'Incoming call' : 'Outgoing call'}
            </Typography>
            {call.result && (
              <Chip
                size="sm"
                variant="soft"
                color={RESULT_COLOR[call.result] ?? 'neutral'}
              >
                {call.result_display || call.result}
              </Chip>
            )}
            {duration && (
              <Typography level="body-xs" color="neutral">
                {duration}
              </Typography>
            )}
            {started && (
              <Typography level="body-xs" color="neutral">
                {format(started, 'h:mm a')}
              </Typography>
            )}
          </Stack>

          <Typography level="body-xs" color="neutral" sx={{ mt: 0.25 }}>
            {formatPhone(counterparty)}
            {call.handled_by_name ? ` · ${call.handled_by_name}` : ''}
          </Typography>

          {call.recording_url && (
            <Box sx={{ mt: 1 }}>
              {audio.src ? (
                <Box
                  component="audio"
                  controls
                  src={audio.src}
                  sx={{ width: '100%', height: 32 }}
                />
              ) : audio.loading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size="sm" />
                  <Typography level="body-xs" color="neutral">
                    Loading recording…
                  </Typography>
                </Stack>
              ) : (
                <Typography level="body-xs" sx={{ color: 'danger.400' }}>
                  {audio.error || 'Recording unavailable'}
                </Typography>
              )}
            </Box>
          )}

          {call.transcription && (
            <Typography
              level="body-sm"
              sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}
            >
              “{call.transcription}”
            </Typography>
          )}

          {hasVoicemail && !call.transcription && call.recording_duration ? (
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
              Voicemail · {formatDuration(call.recording_duration)}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Sheet>
  );
};

export default CallTimelineCard;
